import api from "../../../../services/api";

/** Show a message to the operator in the app-wide snackbar. */
const notifyOperator = (message, severity = "info") => {
  try {
    window.dispatchEvent(
      new CustomEvent("showGlobalMessage", { detail: { message, severity } })
    );
  } catch (e) {
    console.error(message);
  }
};

/** Pull the most useful message out of an axios error. Operator endpoints reply with plain text. */
const errorMessage = error => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.error) return data.error;
  if (data?.message) return data.message;
  return error?.message || "שגיאה לא ידועה";
};

/**
 * POST to an operator endpoint.
 *
 * Failures are reported to the operator and resolved rather than thrown: an unhandled
 * rejection here shows up as a full-screen "Request failed with status code 400" overlay
 * instead of telling the operator what actually went wrong.
 *
 * @param {string} path
 * @param {Object} [body]
 * @param {{errorPrefix?: string}} [options]
 * @returns {Promise<{ok: boolean, data?: any, error?: string}>}
 */
const operatorPost = async (path, body = {}, { errorPrefix = "" } = {}) => {
  try {
    const response = await api.post(path, body);
    return { ok: true, data: response.data };
  } catch (error) {
    const message = errorMessage(error);
    notifyOperator(errorPrefix ? `${errorPrefix}: ${message}` : message, "error");
    return { ok: false, error: message };
  }
};

// `immediate` moves the test-taker right away and pops a message up on their screen.
// Without it the move is queued and only lands once they answer the current question and
// press "next" — the original behaviour, kept for when the operator would rather not interrupt.
const postMoveToNextChapter = (userID, immediate = true) =>
  operatorPost("/operator/next-chapter", { userID, immediate }, { errorPrefix: "מעבר לפרק הבא נכשל" });

const postMoveToNextTest = (userID, immediate = true) =>
  operatorPost("/operator/next-test", { userID, immediate }, { errorPrefix: "מעבר למבחן הבא נכשל" });

const postAllowToiletBrake = userID =>
  operatorPost("/operator/allow-toilet-brake", { userID }, { errorPrefix: "אישור הפסקה נכשל" });

const postAnswerRaisedHandQuestion = (userID, questionID, queueID, answer) =>
  operatorPost(
    "/operator/answered-raised-hand-question",
    { userID, questionID, queueID, answer },
    { errorPrefix: "שליחת התשובה נכשלה" }
  );

const postCloseUserQueue = (userID, queueID) =>
  operatorPost("/operator/close-user-queue", { userID, queueID }, { errorPrefix: "סגירת הפנייה נכשלה" });

const postMarkAllRead = userID =>
  operatorPost("/operator/mark-all-read", { userID }, { errorPrefix: "סימון הפניות כנקראו נכשל" });

const postChangeTestTime = (userID, minutes, shouldIncrease) =>
  operatorPost(
    "/operator/change-test-time",
    { userID, minutes, shouldIncrease },
    { errorPrefix: "שינוי זמן המבחן נכשל" }
  );

const postChangeReferencesTime = (userID, minutes, shouldIncrease) =>
  operatorPost(
    "/operator/change-reference-time",
    { userID, minutes, shouldIncrease },
    { errorPrefix: "שינוי זמן ההפניות נכשל" }
  );

const postChangeChapterTime = (userID, minutes, shouldIncrease) =>
  operatorPost(
    "/operator/change-chapter-time",
    { userID, minutes, shouldIncrease },
    { errorPrefix: "שינוי זמן הפרק נכשל" }
  );

const postMoveToPreviousTest = (userID, immediate = true) =>
  operatorPost("/operator/previous-test", { userID, immediate }, { errorPrefix: "חזרה למבחן הקודם נכשלה" });

const postMoveToPreviousChapter = (userID, immediate = true) =>
  operatorPost("/operator/previous-chapter", { userID, immediate }, { errorPrefix: "חזרה לפרק הקודם נכשלה" });

// Back to the top of the chapter they are already in — `previous-chapter` goes one chapter
// too far back. The server tops their timer up on arrival so they don't land on 00:00.
const postReturnToChapterStart = (userID, immediate = true) =>
  operatorPost(
    "/operator/return-to-chapter-start",
    { userID, immediate },
    { errorPrefix: "חזרה לתחילת הפרק נכשלה" }
  );

const postMoveToChapterRefrences = (userID, immediate = true) =>
  operatorPost("/operator/previous-refrences", { userID, immediate }, { errorPrefix: "חזרה לדף ההפניות נכשלה" });

const postSendMessageToEveryone = message =>
  operatorPost("/operator/send-message-to-everyone", { message }, { errorPrefix: "שליחת ההודעה נכשלה" });

const postStopEveryoneTest = () =>
  operatorPost("/operator/end-all-questionnaires", {}, { errorPrefix: "עצירת המבחנים נכשלה" });

const postRestartEveryoneTest = () =>
  operatorPost("/operator/restart-all-questionnaires", {}, { errorPrefix: "הפעלת המבחנים מחדש נכשלה" });

const postSendMessageToSomeone = (userID, message) =>
  operatorPost("/operator/send-message-to-someone", { userID, message }, { errorPrefix: "שליחת ההודעה נכשלה" });

const postForcePauseUser = userID =>
  operatorPost("/operator/force-pause-user", { userID }, { errorPrefix: "עצירת המבחן נכשלה" });

const postForceResumeUser = userID =>
  operatorPost("/operator/force-resume-user", { userID }, { errorPrefix: "המשך המבחן נכשל" });

const postSendPopupToSomeone = (userID, message) =>
  operatorPost("/operator/send-popup-to-someone", { userID, message }, { errorPrefix: "שליחת הפופאפ נכשלה" });

const postReturnFromToilet = userID =>
  operatorPost("/operator/return-from-toilet-brake", { userID }, { errorPrefix: "החזרה מהפסקה נכשלה" });

const postSetOverrideNext = (userID, targetPosition, immediate = true) =>
  operatorPost("/operator/set-override-next", { userID, targetPosition, immediate }, { errorPrefix: "הזזת הנבחן נכשלה" });

// --- Global (bulk) operations ---
//
// Each of these applies a per-user action to many users at once. On success `data` is
// `{ total, succeeded: [{id, name}], skipped: [{id, name, reason}] }` — users that
// couldn't be changed are reported per user rather than failing the whole request.
// `userIDs` may be omitted (or empty) to target every active test-taker.

const postBulkChangeTime = (section, minutes, shouldIncrease, userIDs) =>
  operatorPost(
    "/operator/bulk/change-time",
    { section, minutes, shouldIncrease, userIDs },
    { errorPrefix: "שינוי הזמן לכולם נכשל" }
  );

const postBulkSetOverrideNext = (targetPosition, questionnaireName, userIDs, immediate = true) =>
  operatorPost(
    "/operator/bulk/set-override-next",
    { targetPosition, questionnaireName, userIDs, immediate },
    { errorPrefix: "הזזת הנבחנים נכשלה" }
  );

const postBulkSkip = (skipType, userIDs, immediate = true) =>
  operatorPost("/operator/bulk/skip", { skipType, userIDs, immediate }, { errorPrefix: "דילוג לכולם נכשל" });

const postBulkSendPopup = (message, userIDs) =>
  operatorPost("/operator/bulk/send-popup", { message, userIDs }, { errorPrefix: "שליחת הפופאפ לכולם נכשלה" });

const postBulkSendMessage = (message, userIDs) =>
  operatorPost("/operator/bulk/send-message", { message, userIDs }, { errorPrefix: "שליחת ההודעה לכולם נכשלה" });

const postBulkMarkAllRead = userIDs =>
  operatorPost("/operator/bulk/mark-all-read", { userIDs }, { errorPrefix: "סימון הפניות כנקראו נכשל" });

const postBulkSetPaused = (paused, userIDs) =>
  operatorPost(
    "/operator/bulk/set-paused",
    { paused, userIDs },
    { errorPrefix: paused ? "עצירת המבחנים נכשלה" : "המשך המבחנים נכשל" }
  );

export {
  notifyOperator,
  postAllowToiletBrake,
  postAnswerRaisedHandQuestion,
  postChangeChapterTime,
  postChangeReferencesTime,
  postChangeTestTime,
  postCloseUserQueue,
  postMarkAllRead,
  postMoveToChapterRefrences,
  postMoveToNextChapter,
  postMoveToNextTest,
  postMoveToPreviousChapter,
  postMoveToPreviousTest,
  postRestartEveryoneTest,
  postReturnToChapterStart,
  postSendMessageToEveryone,
  postSendMessageToSomeone,
  postSendPopupToSomeone,
  postForcePauseUser,
  postForceResumeUser,
  postStopEveryoneTest,
  postReturnFromToilet,
  postSetOverrideNext,
  postBulkChangeTime,
  postBulkSetOverrideNext,
  postBulkSkip,
  postBulkSendPopup,
  postBulkSendMessage,
  postBulkMarkAllRead,
  postBulkSetPaused
};
